import React, { FC, Fragment, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router";
import { PersonalizationReducerState } from "src/store/personalization/types";
import { useBooleanState } from "../../../hooks/utils";
import { LoginDTO } from "../../../requests/login/types";
import ChangeLanguage from "../../molecules/ChangeLanguage";
import { StyledNotificationIndicator } from "../NavMenu/RightSection/Notifications/styles";
import MappedDataSources from "./components/MappedDataSources/MappedDataSources";
import RNativeSection from "./RNativeSection";
import {
  ExpandIcon,
  LogoutSectionContainer,
  SectionFooterContainer,
  SectionLink,
  SectionRow,
} from "./styles";
import UserSection from "./UserSection";

export type LogOutSectionProps = {
  user: LoginDTO | null;
  personalizationData: PersonalizationReducerState | null;
  nameOfImpersonatedUser?: string | null;
  unreportedBugsCount: number;
  hasFlowioBugs: boolean;
  requestLogout: () => void;
  stopImpersonating: () => void;
  invokeBugReportModal: () => void;
  invokeFeedbackModal: () => void;
  invokePasswordChangeModal: () => void;
  invokeBugsOverviewModal: () => void;
};

const LogOutSection: FC<LogOutSectionProps> = ({
  user,
  personalizationData,
  nameOfImpersonatedUser,
  unreportedBugsCount,
  hasFlowioBugs,
  requestLogout,
  stopImpersonating,
  invokeBugReportModal,
  invokeFeedbackModal,
  invokePasswordChangeModal,
  invokeBugsOverviewModal,
}) => {
  const [listToggled, toggleList] = useBooleanState(false);
  const [sourcesLength, setSourcesLength] = useState(0);
  const { push } = useHistory();

  const handleChangePassword = (
    e: React.MouseEvent<HTMLButtonElement>
  ): void => {
    e.preventDefault();
    invokePasswordChangeModal();
  };

  const handleChangeBugOverview = (
    e: React.MouseEvent<HTMLButtonElement>
  ): void => {
    e.preventDefault();
    invokeBugsOverviewModal();
  };

  return (
    <Fragment>
      {user && (
        <LogoutSectionContainer data-cy="user-menu">
          <UserSection
            requestLogout={requestLogout}
            login={user.login}
            userName={user.userName}
            nameOfImpersonatedUser={nameOfImpersonatedUser}
            stopImpersonating={stopImpersonating}
          />
          <RNativeSection />
          <SectionRow>
            <SectionLink
              data-cy="personal-settings"
              onClick={(): void => push("/settings")}
            >
              <FormattedMessage id="login.personal_settings" />
            </SectionLink>
          </SectionRow>
          <SectionRow>
            <SectionLink
              data-cy="change-password"
              onClick={handleChangePassword}
            >
              <FormattedMessage id="login.change_password" />
            </SectionLink>
          </SectionRow>
          {!!personalizationData?.bugReportVisible && (
            <SectionRow>
              <SectionLink
                data-cy="bugReport"
                onClick={() => invokeBugReportModal()}
              >
                <FormattedMessage id="report_a_bug" />
              </SectionLink>
            </SectionRow>
          )}
          {!!personalizationData?.feedbackVisible && (
            <SectionRow>
              <SectionLink data-cy="sendFeedback" onClick={invokeFeedbackModal}>
                <FormattedMessage id="send_feedback" />
              </SectionLink>
            </SectionRow>
          )}
          {hasFlowioBugs && (
            <SectionRow>
              <StyledNotificationIndicator
                onClick={handleChangeBugOverview}
                notificationCount={unreportedBugsCount}
              >
                {unreportedBugsCount}
              </StyledNotificationIndicator>
              <SectionLink onClick={handleChangeBugOverview}>
                <FormattedMessage id="login.bugs_overview" />
              </SectionLink>
            </SectionRow>
          )}
          <ChangeLanguage />
          <MappedDataSources
            toggled={listToggled}
            updateSourcesLength={setSourcesLength}
          />
          {sourcesLength > 2 && (
            <SectionFooterContainer>
              <span>
                {listToggled ? (
                  <FormattedMessage id="common.less" />
                ) : (
                  <FormattedMessage id="common.more" />
                )}
              </span>
              <ExpandIcon
                data-cy="expand-list"
                toggled={listToggled}
                onClick={toggleList}
              />
            </SectionFooterContainer>
          )}
        </LogoutSectionContainer>
      )}
    </Fragment>
  );
};

export default LogOutSection;
